import { useQueryClient } from '@tanstack/react-query'
import { createContext, PropsWithChildren, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { TokenResponse } from 'src/api/requests'
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  RESET_EMAIL,
  USER_ID_KEY,
  USER_INSTITUTION_KEY,
  USER_ROLE_KEY
} from 'src/app-constants'
import { useLocalStorage } from 'src/hooks'
import { paths } from 'src/paths'

const defaultContextValue = {
  session: null,
  onLogin: () => {},
  onLogout: () => {},
  onResetPassword: () => {}
}

const SessionContext = createContext<{
  session: TokenResponse | null
  onLogin: (session: TokenResponse) => void
  onLogout: () => void
  onResetPassword: (email: string) => void
}>(defaultContextValue)

export const useSessionContext = () => {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error(
      'useSessionContext must be used within a SessionContextProvider'
    )
  }
  return context
}

export const SessionContextProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate()

  const {
    storedValue: accessToken,
    setValue: setAccessToken,
    remove: removeAccessToken
  } = useLocalStorage(ACCESS_TOKEN_KEY, '')

  const {
    storedValue: refreshToken,
    setValue: setRefreshToken,
    remove: removeRefreshToken
  } = useLocalStorage(REFRESH_TOKEN_KEY, '')

  const {
    storedValue: userId,
    setValue: setUserId,
    remove: removeUserId
  } = useLocalStorage(USER_ID_KEY, '')

  const { setValue: setResetEmail } = useLocalStorage(RESET_EMAIL, '')

  const [session, setSession] = useState<TokenResponse | null>(() => {
    if (accessToken && refreshToken && userId) {
      return {
        accessToken,
        refreshToken,
        userId
      }
    }
    return null
  })

  const queryClient = useQueryClient()

  const onLogin = (session: TokenResponse) => {
    setSession(session)
    if (session.accessToken && session.refreshToken && session.userId) {
      setAccessToken(session.accessToken)
      setRefreshToken(session.refreshToken)
      setUserId(session.userId)
    }
    navigate(paths.root)
  }

  const onResetPassword = (email: string) => {
    setResetEmail(email)
  }

  const onLogout = () => {
    removeAccessToken()
    removeRefreshToken()
    removeUserId()
    localStorage.removeItem(USER_ROLE_KEY)
    localStorage.removeItem(USER_INSTITUTION_KEY)
    setSession(null)
    queryClient.removeQueries()
  }

  return (
    <SessionContext.Provider
      value={{
        session,
        onLogin,
        onLogout,
        onResetPassword
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}
